import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Flex,
  HStack,
  Spinner,
  Alert,
  AlertIcon,
  Text,
  useDisclosure,
  useToast,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useThemeContext } from '@/contexts/ThemeContext';
import { useApi } from '@/contexts/ApiContext';
// Legacy note: previous implementation used useTeamService; now TeamApi from ApiContext is used
import type { CordeCommonModelsDtoTeamTeam } from '@/api/generated/v1/api';

// Components
import { TeamHeader } from '@/components/team/TeamHeader';
import { TeamSidebarManager } from '@/components/team/TeamSidebarManager';
import { TeamOverviewTab } from '@/components/team/TeamOverviewTab';
import { TeamMembersTab } from '@/components/team/TeamMembersTab';
import { TeamInvitationsTab } from '@/components/team/TeamInvitationsTab';
import { TeamRolesTab } from '@/components/team/TeamRolesTab';
import { TeamRequestsTab } from '@/components/team/TeamRequestsTab';
import { TeamContactsTab } from '@/components/team/TeamContactsTab';
import { TeamAuditLogsTab } from '@/components/team/TeamAuditLogsTab';
import { TeamSettingsTab } from '@/components/team/TeamSettingsTab';
import { TeamInviteModal } from '@/components/team/TeamInviteModal';
import { TeamEditModal } from '@/components/team/TeamEditModal';
import { TeamRoleEditModal } from '@/components/team/TeamRoleEditModal';

interface TeamDetails {
  team: CordeCommonModelsDtoTeamTeam;
  permissions: {
    view_team: boolean;
    update_team: boolean;
    delete_team: boolean;
    invite_user_to_team: boolean;
    add_user_to_team: boolean;
    remove_user_from_team: boolean;
    view_team_members: boolean;
    view_team_audit_logs: boolean;
    manage_team_settings: boolean;
    view_team_settings: boolean;
    list_team_contacts: boolean;
    add_team_contact: boolean;
    update_team_contact: boolean;
    delete_team_contact: boolean;
    transfer_team_ownership: boolean;
    create_subteam: boolean;
    create_team_avatar: boolean;
    delete_team_avatar: boolean;
    create_team_banner: boolean;
    delete_team_banner: boolean;
  };
  counters: {
    members_count: number;
    invitations_count: number;
    roles_count: number;
    membership_requests_count: number;
    role_requests_count: number;
    ownership_requests_count: number;
    contacts_count: number;
    audit_logs_count: number;
    child_teams_count: number;
  };
}

export const TeamPage: React.FC = () => {
  const { t } = useTranslation();
  const { teamId } = useParams<{ teamId: string }>();
  const toast = useToast();
  const { colorSchemes } = useThemeContext();
  const { TeamApi: teamService } = useApi();
  
  // State
  const [teamDetails, setTeamDetails] = useState<TeamDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<string>('overview');
  const [isLoadingAvatarBanner, setIsLoadingAvatarBanner] = useState(false);
  const [selectedRole, setSelectedRole] = useState<any>(null);
  
  // Modal states
  const { isOpen: isInviteOpen, onOpen: onInviteOpen, onClose: onInviteClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isRoleEditOpen, onOpen: onRoleEditOpen, onClose: onRoleEditClose } = useDisclosure();
  
  // Load team data
  const loadTeamData = async () => {
    if (!teamId) return;

    try {
      setIsLoading(true);
      // Using new generated API: getTeam returns AxiosResponse with team data
      const response = await teamService.getTeam(teamId);
      const team = (response as any).data ?? response; // fallback if api already returns data directly

      // Backend endpoints for permissions and counters are not yet available; using safe defaults
      const mockPermissions = {
        view_team: true,
        update_team: false,
        delete_team: false,
        invite_user_to_team: false,
        add_user_to_team: false,
        remove_user_from_team: false,
        view_team_members: true,
        view_team_audit_logs: false,
        manage_team_settings: false,
        view_team_settings: true,
        list_team_contacts: false,
        add_team_contact: false,
        update_team_contact: false,
        delete_team_contact: false,
        transfer_team_ownership: false,
        create_subteam: false,
        create_team_avatar: false,
        delete_team_avatar: false,
        create_team_banner: false,
        delete_team_banner: false,
      };

      const emptyCounters = {
        members_count: 0,
        invitations_count: 0,
        roles_count: 0,
        membership_requests_count: 0,
        role_requests_count: 0,
        ownership_requests_count: 0,
        contacts_count: 0,
        audit_logs_count: 0,
        child_teams_count: 0,
      };

      setTeamDetails({
        team,
        permissions: mockPermissions,
        counters: emptyCounters,
      });
      setError(null);
    } catch (err: any) {
      setError(err.message || t('teams.failedToLoadTeam'));
      toast({
        title: t('common.error'),
        description: err.message || t('teams.failedToLoadTeam'),
        status: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle section change
  const handleSectionChange = (section: string) => {
    setActiveSection(section);
  };

  // Avatar and Banner handlers
  const getAvatarUrl = () => (teamDetails?.team as any)?.avatar_url;
  const getBannerUrl = () => (teamDetails?.team as any)?.banner_url;

  const handleAvatarUpload = async (file: File) => {
    if (!teamId) return;
    
    try {
      setIsLoadingAvatarBanner(true);
      await teamService.createTeamAvatar(teamId, file);
      await loadTeamData();
      toast({ title: t('teams.avatarUpdated'), status: 'success' });
    } catch (err: any) {
      toast({ title: t('common.error'), description: err.message, status: 'error' });
    } finally {
      setIsLoadingAvatarBanner(false);
    }
  };

  const handleBannerUpload = async (file: File) => {
    if (!teamId) return;
    
    try {
      setIsLoadingAvatarBanner(true);
      await teamService.createTeamBanner(teamId, file);
      await loadTeamData();
      toast({ title: t('teams.bannerUpdated'), status: 'success' });
    } catch (err: any) {
      toast({ title: t('common.error'), description: err.message, status: 'error' });
    } finally {
      setIsLoadingAvatarBanner(false);
    }
  };

  const handleAvatarDelete = async () => {
    if (!teamId) return;
    
    try {
      setIsLoadingAvatarBanner(true);
      // await teamService.deleteTeamAvatar(teamId);
      await loadTeamData();
      toast({ title: t('teams.avatarDeleted'), status: 'success' });
    } catch (err: any) {
      toast({ title: t('common.error'), description: err.message, status: 'error' });
    } finally {
      setIsLoadingAvatarBanner(false);
    }
  };

  const handleBannerDelete = async () => {
    if (!teamId) return;
    
    try {
      setIsLoadingAvatarBanner(true);
      await teamService.deleteTeamBanner(teamId);
      await loadTeamData();
      toast({ title: t('teams.bannerDeleted'), status: 'success' });
    } catch (err: any) {
      toast({ title: t('common.error'), description: err.message, status: 'error' });
    } finally {
      setIsLoadingAvatarBanner(false);
    }
  };

  // Role management handlers
  const handleEditRole = (role: any) => {
    setSelectedRole(role);
    onRoleEditOpen();
  };

  const handleDeleteRole = async (roleId: string) => {
    if (!teamId) return;
    
    try {
      // Role deletion is not yet implemented on the backend side; showing info toast for now
      toast({
        title: t('common.notImplemented'),
        description: 'Role deletion will be implemented soon',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
    } catch (err: any) {
      toast({ title: t('common.error'), description: err.message, status: 'error' });
    }
  };

  // Adapter function to convert new permissions to old format for components
  const adaptPermissionsForComponents = (permissions: any) => {
    return {
      // TeamMembersTab permissions
      can_view_team: permissions.view_team,
      can_add_members: permissions.add_user_to_team,
      can_remove_members: permissions.remove_user_from_team,
      can_invite_users: permissions.invite_user_to_team,
      can_manage_roles: permissions.manage_team_settings,
      
      // TeamInvitationsTab permissions
      can_view_invitations: permissions.view_team_members,
      can_cancel_invitation: permissions.manage_team_settings,
      
      // TeamRolesTab permissions
      can_create_role: permissions.manage_team_settings,
      can_update_role: permissions.manage_team_settings,
      can_delete_role: permissions.manage_team_settings,
      
      // TeamRequestsTab permissions
      can_view_membership_requests: permissions.view_team_members,
      can_approve_membership: permissions.add_user_to_team,
      
      // TeamContactsTab permissions
      can_manage_contacts: permissions.list_team_contacts,
      can_add_contact: permissions.add_team_contact,
      can_update_contact: permissions.update_team_contact,
      can_delete_contact: permissions.delete_team_contact,
      
      // TeamAuditLogsTab permissions
      can_view_audit_logs: permissions.view_team_audit_logs,
    };
  };

  // Render current section content
  const renderSectionContent = () => {
    if (!teamDetails) return null;

    const { team, counters, permissions } = teamDetails;
    const adaptedPermissions = adaptPermissionsForComponents(permissions);

    switch (activeSection) {
      case 'overview':
        return <TeamOverviewTab team={team} counters={counters} />;
      case 'members':
        return (
          <TeamMembersTab
            teamId={teamId!}
            permissions={adaptedPermissions}
            isActive={activeSection === 'members'}
          />
        );
      case 'invitations':
        return (
          <TeamInvitationsTab
            teamId={teamId!}
            permissions={adaptedPermissions}
            isActive={activeSection === 'invitations'}
          />
        );
      case 'roles':
        return (
          <TeamRolesTab
            teamId={teamId!}
            permissions={adaptedPermissions}
            isActive={activeSection === 'roles'}
            onEditRole={handleEditRole}
            onDeleteRole={handleDeleteRole}
          />
        );
      case 'requests':
        return (
          <TeamRequestsTab
            teamId={teamId!}
            permissions={adaptedPermissions}
            isActive={activeSection === 'requests'}
          />
        );
      case 'contacts':
        return (
          <TeamContactsTab
            teamId={teamId!}
            permissions={adaptedPermissions}
            isActive={activeSection === 'contacts'}
          />
        );
      case 'audit-logs':
        return (
          <TeamAuditLogsTab
            teamId={teamId!}
            permissions={adaptedPermissions}
            isActive={activeSection === 'audit-logs'}
          />
        );
      case 'settings':
        return <TeamSettingsTab team={team} />;
      default:
        return <TeamOverviewTab team={team} counters={counters} />;
    }
  };

  // Effects
  useEffect(() => {
    loadTeamData();
  }, [teamId]);

  // Loading state
  if (isLoading) {
    return (
      <Container maxW="7xl" py={8}>
        <VStack spacing={8}>
          <Spinner size="xl" />
          <Text>{t('teams.loadingTeamDetails')}</Text>
        </VStack>
      </Container>
    );
  }

  // Error state
  if (error || !teamDetails?.team) {
    return (
      <Container maxW="7xl" py={8}>
        <Alert status="error">
          <AlertIcon />
          {error || t('teams.teamNotFound')}
        </Alert>
      </Container>
    );
  }

  return (
    <Box minH="100vh" pb={8}>
      <Container maxW="6xl" py={8} px={4}>
        <VStack spacing={8} align="stretch">
          {/* Team Header */}
          <TeamHeader
            team={teamDetails.team}
            permissions={teamDetails.permissions}
            getAvatarUrl={getAvatarUrl}
            getBannerUrl={getBannerUrl}
            isLoadingAvatarBanner={isLoadingAvatarBanner}
            onAvatarUpload={handleAvatarUpload}
            onBannerUpload={handleBannerUpload}
            onAvatarDelete={handleAvatarDelete}
            onBannerDelete={handleBannerDelete}
          />

          {/* Section Content */}
          <Box>
            {renderSectionContent()}
          </Box>
        </VStack>

        {/* Sidebar Manager */}
        <TeamSidebarManager
          activeSection={activeSection}
          teamDetails={teamDetails}
          onSectionChange={handleSectionChange}
          onInviteOpen={onInviteOpen}
          onEditOpen={onEditOpen}
        />

        {/* Modals */}
        <TeamInviteModal
          isOpen={isInviteOpen}
          onClose={onInviteClose}
          teamId={teamId!}
          onSuccess={loadTeamData}
        />

        <TeamEditModal
          isOpen={isEditOpen}
          onClose={onEditClose}
          team={teamDetails.team}
          onSuccess={loadTeamData}
        />

        <TeamRoleEditModal
          isOpen={isRoleEditOpen}
          onClose={onRoleEditClose}
          role={selectedRole}
          teamId={teamId!}
          onSuccess={loadTeamData}
        />
      </Container>
    </Box>
  );
}; 